/**
 * Centralized TanStack Query keys.
 *
 * Using a single factory keeps cache keys consistent across screens and makes
 * invalidation precise (e.g. after a self-score submit we invalidate the call
 * detail + its ai-reveal so the gate flips). All keys are arrays so they nest
 * cleanly for partial invalidation.
 */
import type { CalibrationFilters, CallListFilters } from "@/types";

export const queryKeys = {
  health: ["health"] as const,

  calls: {
    all: ["calls"] as const,
    list: (filters: CallListFilters = {}) =>
      ["calls", "list", filters] as const,
    // Dashboard KPI aggregates (GET /api/calls/stats), keyed by the same filters.
    stats: (filters: CallListFilters = {}) =>
      ["calls", "stats", filters] as const,
    detail: (id: string) => ["calls", "detail", id] as const,
    transcript: (id: string) => ["calls", "transcript", id] as const,
    // AI reveal is a separate key because it is gated and fetched independently
    // once the self-score is submitted (see CallDetail flow).
    aiReveal: (id: string) => ["calls", "ai-reveal", id] as const,
    recordingUrl: (id: string) => ["calls", "recording-url", id] as const,
    // The rep's self-score (draft or submitted) + rubric. Invalidate alongside
    // detail + aiReveal after a submit so the gate flips.
    selfScore: (id: string) => ["calls", "self-score", id] as const,
    // Timestamped feedback notes keyed to the audio (self + manager).
    notes: (id: string) => ["calls", "notes", id] as const,
    // Manager flag + score override for the call (coaching display panel).
    coaching: (id: string) => ["calls", "coaching", id] as const,
  },

  rubric: {
    current: ["rubric", "current"] as const,
    version: (version: number) => ["rubric", "version", version] as const,
  },

  manager: {
    // Per-rep score trends for the manager dashboard.
    dashboard: ["manager", "dashboard"] as const,
    // Open coaching items (calls with an open flag).
    queue: ["manager", "queue"] as const,
    // Active staff/manager for the assign dropdown.
    staff: ["manager", "staff"] as const,
    // Per-rep agent profile (CP3), keyed by rep id + the date window.
    agent: (id: string, filters: CalibrationFilters = {}) =>
      ["manager", "agent", id, filters] as const,
  },

  // The caller's own saved Dashboard views (per-user, CP3).
  views: ["views"] as const,
} as const;
